import { getInstruction, BankFormState, CompanyFormState, CompanyProfile, PostalAndVisitingAddress, CompanyFieldMetaData } from "shared/utils/fieldsHelper/companyFields";
import { PostalAddressForm } from "company/detail/components/PostalAddressForm";
import { PostalAddress, VisitingAddress } from "company/detail/components/PostalOrVisitingAddress";
import { ProfilePicker } from "company/detail/components/ProfilePicker";
import ProfileRow from "company/detail/components/ProfileRow";
import { VisitingAddressForm } from "company/detail/components/VisitingAddressForm";
import { Status, StatusDropdown } from "components";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import { DeletedRow } from "components/DetailPage/DeletedRow";
import LocalTimeRow from "components/DetailPage/LocalTimeRow";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import { FormSection } from "components/MainSectionTemplate/types";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import TagList from "components/Tag/TagList";
import TagsSelect from "components/Tag/TagsSelect";
import React from "react";
import { isAdminAndVisitingAddressSame } from "shared/utils/companyUtils";

export const companyMainSection = (
  companyFormState: CompanyFormState,
  bankFormState: BankFormState,
  fieldMetaData: CompanyFieldMetaData,
  isSidePanel?: boolean
): FormSection => {
  return {
    key: "companyMainDetails",
    type: "regular",
    title: RESX.Company.resxCompany,
    fields: [{
      ...fieldMetaData.deleted.fields(),
      key: "deleted",
      label: RESX.File.Deleted,
      // The side panel renders these two as their own sections
      hidden: isSidePanel || fieldMetaData.deleted.fields().hidden,
      render: () => <DeletedRow />,
    }, {
      ...fieldMetaData.isValidated.fields(),
      key: "isValidated",
      label: RESX.Planning.ExternalInput,
      hidden: isSidePanel || fieldMetaData.isValidated.fields().hidden,
      render: () => <NotValidatedRow
        canApprove={!fieldMetaData.isValidated.fields().editDisabled}
        sublabel={RESX.Planning.InputFromWebform}
        approveEndpoint={() => window.CoreApi.Company.Validate({
          companyId: companyFormState.companyId
        })}
      />,
    }, {
      ...fieldMetaData.visitingAddress.fields(),
      key: "visitingAddress",
      label: RESX.Address.resxAddress,
      render: () => <VisitingAddress companyFormState={companyFormState} />,
      inPageEdit: {
        useCustomSavingState: true,
        fullWidth: true,
        render: ({ value, onChange, submit, isSaving }) => <VisitingAddressForm
          submit={submit}
          onChange={onChange}
          value={value}
          isSaving={isSaving}
        />,
        initialValue: companyFormState.address,
        type: "custom",
        submitChange: (value: PostalAndVisitingAddress) => {
          const isSameAsVisitingAddress = isAdminAndVisitingAddressSame(companyFormState);
          const instruction = getInstruction(companyFormState, bankFormState, {
            regularName: value.visitingAddress.companyName,
            regularAddress: {
              addressLine1: value.visitingAddress.addressLine1,
              addressLine2: value.visitingAddress.addressLine2,
              city: value.visitingAddress.city,
              postalCode: value.visitingAddress.postalCode,
              stateId: value.visitingAddress.state.key,
            },
            administrativeName: isSameAsVisitingAddress ? value.visitingAddress.companyName : value.postalAddress.companyName,
            administrativeAddress: {
              addressLine1: isSameAsVisitingAddress ? value.visitingAddress.addressLine1 : value.postalAddress.addressLine1,
              addressLine2: isSameAsVisitingAddress ? value.visitingAddress.addressLine2 : value.postalAddress.addressLine2,
              city: isSameAsVisitingAddress ? value.visitingAddress.city : value.postalAddress.city,
              postalCode: isSameAsVisitingAddress ? value.visitingAddress.postalCode : value.postalAddress.postalCode,
              stateId: isSameAsVisitingAddress ? value.visitingAddress.state.key : value.postalAddress.state.key,
            }
          });

          return window.CoreApi.Company.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.postalAddress.fields(),
      key: "postalAddress",
      label: RESX.Company.PostalAddress,
      render: () => <PostalAddress companyFormState={companyFormState} />,
      inPageEdit: {
        useCustomSavingState: true,
        fullWidth: true,
        render: ({ value, onChange, submit, isSaving }) => <PostalAddressForm
          value={value}
          onChange={onChange}
          submit={submit}
          isSaving={isSaving}
        />,
        initialValue: companyFormState.address,
        type: "custom",
        submitChange: (value: PostalAndVisitingAddress) => {
          const instruction = getInstruction(companyFormState, bankFormState, {
            regularName: value.visitingAddress.companyName,
            regularAddress: {
              addressLine1: value.visitingAddress.addressLine1,
              addressLine2: value.visitingAddress.addressLine2,
              city: value.visitingAddress.city,
              postalCode: value.visitingAddress.postalCode,
              stateId: value.visitingAddress.state.key,
            },
            administrativeName: value.postalAddress.companyName,
            administrativeAddress: {
              addressLine1: value.postalAddress.addressLine1,
              addressLine2: value.postalAddress.addressLine2,
              city: value.postalAddress.city,
              postalCode: value.postalAddress.postalCode,
              stateId: value.postalAddress.state.key,
            }
          });

          return window.CoreApi.Company.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.profile.fields(),
      key: "profile",
      label: RESX.Company.Profile,
      render: () => <ProfileRow profile={companyFormState.profile} />,
      inPageEdit: {
        render: ({ value, onChange }) => <ProfilePicker
          onChange={onChange}
          value={value}
        />,
        type: "confirmOrCancelButton",
        initialValue: companyFormState.profile,
        submitChange: (value: CompanyProfile) => {
          const instruction = getInstruction(companyFormState, bankFormState, {
            isArtist: value.isArtist,
            isPromoter: value.isPromoter,
            isVenue: value.isVenue,
          });

          return window.CoreApi.Company.Update(instruction);
        }

      }
    }, {
      ...fieldMetaData.status.fields(),
      key: "status",
      label: RESX.Status.resxStatus,
      render: () => <Status status={companyFormState.status} />,
      inPageEdit: {
        render: ({ value, onChange }) => (
          <StatusDropdown
            value={value}
            onChange={onChange}
            mainArea="company"
            clientId={companyFormState.clientId}
            options={undefined}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: companyFormState.status,
        fullWidth: true,
        submitChange: (value: server.dto.StatusSmall) => {
          const instruction = getInstruction(companyFormState, bankFormState, {
            statusId: value.statusId,
          });

          return window.CoreApi.Company.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.tags.fields(),
      key: "tags",
      label: RESX.Tag.Tags,
      render: () => <TagList tags={companyFormState.tags} version="long" />,
      inPageEdit: {
        render: ({ value, onChange }) => (
          <TagsSelect
            mainArea="company"
            value={value}
            onChange={onChange}
            inPageEditing="edit"
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: companyFormState.tags,
        submitChange: (tags: server.dto.TagSmall[]) => {
          return window.CoreApi.Company.UpdateTags({
            itemId: companyFormState.companyId,
            tagsIds: tags.map(tag => tag.tagId),
          });
        }
      }
    }, {
      ...fieldMetaData.owner.fields(),
      key: "owner",
      label: RESX.GeneralLabels.Owner,
      render: () => <UserWithAvatar user={companyFormState.owner} />,
      inPageEdit: {
        hoverEdit: true,
        render: ({ onChange, cancel, value }) => (
          <div className="main-section-template-fields-padding">
            <UserModalPicker
              inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
              canCreateNew={false}
            />
          </div>
        ),
        type: "custom",
        initialValue: companyFormState.owner,
        submitChange: (value: server.dto.UserSmall) => {
          const instruction = getInstruction(companyFormState, bankFormState, {
            ownerUserId: value.userId,
          });

          return window.CoreApi.Company.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.localTime.fields(),
      key: "localTime",
      label: RESX.DateTime.LocalTime,
      render: () => <LocalTimeRow ianaTimeZoneId={companyFormState.ianaTimeZoneId} />,
    },],
  }
}